import React, { useState, useEffect } from 'react';
import { Palette, Save, RotateCcw, Eye, Upload, Check, Building2 } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';
import { api } from '../utils/api';

interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  success: string;
  warning: string;
  error: string;
}

interface BrandingConfig {
  company_name: string;
  company_tagline?: string;
  logo_url?: string;
  colors: ColorScheme;
  contact_email?: string;
  contact_phone?: string;
  website_url?: string;
}

interface BrandingSettingsProps {
  orgId: string;
  onSave?: (config: BrandingConfig) => void;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_THEMES: Record<string, { name: string; colors: ColorScheme }> = {
  default: {
    name: 'Purple (Default)',
    colors: {
      primary: '#7c3aed',
      secondary: '#06b6d4',
      accent: '#f59e0b',
      background: '#f8fafc',
      text: '#1e293b',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  ocean: {
    name: 'Ocean Blue',
    colors: {
      primary: '#0ea5e9',
      secondary: '#06b6d4',
      accent: '#14b8a6',
      background: '#f0f9ff',
      text: '#0c4a6e',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  forest: {
    name: 'Forest Green',
    colors: {
      primary: '#16a34a',
      secondary: '#22c55e',
      accent: '#84cc16',
      background: '#f0fdf4',
      text: '#14532d',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  sunset: {
    name: 'Sunset Orange',
    colors: {
      primary: '#ea580c',
      secondary: '#f97316',
      accent: '#fbbf24',
      background: '#fff7ed',
      text: '#7c2d12',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  midnight: {
    name: 'Midnight Purple',
    colors: {
      primary: '#6366f1',
      secondary: '#8b5cf6',
      accent: '#a855f7',
      background: '#faf5ff',
      text: '#3730a3',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  corporate: {
    name: 'Corporate Blue',
    colors: {
      primary: '#1e40af',
      secondary: '#3b82f6',
      accent: '#0ea5e9',
      background: '#f8fafc',
      text: '#1e293b',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  }
};

export default function BrandingSettings({ orgId, onSave }: BrandingSettingsProps) {
  const { refreshBranding } = useBranding();
  const [config, setConfig] = useState<BrandingConfig>({
    company_name: '',
    company_tagline: '',
    logo_url: '',
    colors: PRESET_THEMES.default.colors,
    contact_email: '',
    contact_phone: '',
    website_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string>('default');
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, [orgId]);

  const fetchBranding = async () => {
    try {
      const data = await api.get(`/api/v1/branding/${orgId}`, { showErrorToast: false });
      if (data.config) {
        setConfig(data.config);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Check if branding exists
      let exists = false;
      try {
        const checkData = await api.get(`/api/v1/branding/${orgId}`, { showErrorToast: false });
        exists = checkData.id !== 'default';
      } catch {
        exists = false;
      }

      if (exists) {
        await api.put(`/api/v1/branding/${orgId}`, config);
      } else {
        await api.post('/api/v1/branding/', { org_id: orgId, config });
      }

      setHasChanges(false);
      onSave?.(config);
      // Refresh the global branding context to apply changes across the app
      await refreshBranding();
      setSaveMessage('Branding saved and applied successfully!');
    } catch (error: any) {
      console.error('Error saving branding:', error);
      setSaveError(error.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeSelect = (themeName: string) => {
    setSelectedTheme(themeName);
    setConfig({
      ...config,
      colors: PRESET_THEMES[themeName].colors
    });
    setHasChanges(true);
  };

  const handleColorChange = (key: keyof ColorScheme, value: string) => {
    setConfig({
      ...config,
      colors: { ...config.colors, [key]: value }
    });
    setSelectedTheme('custom');
    setHasChanges(true);
  };

  const handleInputChange = (key: keyof BrandingConfig, value: string) => {
    setConfig({ ...config, [key]: value });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="rounded-xl shadow-lg p-8 animate-pulse" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="h-6 rounded w-1/3 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
        <div className="space-y-3">
          <div className="h-10 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
          <div className="h-10 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      {/* Header */}
      <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">White-Label Settings</h2>
              <p className="text-sm" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>Customize branding for your organization</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-4 py-2 rounded-lg transition flex items-center gap-2 font-semibold disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)' }}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Building2 className="w-4 h-4" />
            Company Information
          </h3>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Company Name *</label>
            <input
              type="text"
              value={config.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="Your Company Name"
              className="w-full px-4 py-2 rounded-lg"
              style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Tagline</label>
            <input
              type="text"
              value={config.company_tagline || ''}
              onChange={(e) => handleInputChange('company_tagline', e.target.value)}
              placeholder="Professional Business Interruption Claims"
              className="w-full px-4 py-2 rounded-lg"
              style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Logo URL</label>
            <input
              type="url"
              value={config.logo_url || ''}
              onChange={(e) => handleInputChange('logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full px-4 py-2 rounded-lg"
              style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            />
            {config.logo_url && (
              <div className="mt-2 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-base)' }}>
                <img 
                  src={config.logo_url} 
                  alt="Logo preview" 
                  className="max-h-16 object-contain"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Contact Email</label>
              <input
                type="email"
                value={config.contact_email || ''}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                placeholder="contact@company.com"
                className="w-full px-4 py-2 rounded-lg"
                style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Contact Phone</label>
              <input
                type="tel"
                value={config.contact_phone || ''}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-2 rounded-lg"
                style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Website URL</label>
            <input
              type="url"
              value={config.website_url || ''}
              onChange={(e) => handleInputChange('website_url', e.target.value)}
              placeholder="https://www.company.com"
              className="w-full px-4 py-2 rounded-lg"
              style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            />
          </div>
        </div>

        {/* Color Scheme */}
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Palette className="w-4 h-4" />
            Color Scheme
          </h3>

          {/* Theme Presets */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Theme Presets</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(PRESET_THEMES).map(([key, theme]) => (
                <button
                  key={key}
                  onClick={() => handleThemeSelect(key)}
                  className="p-3 rounded-lg border-2 transition"
                  style={
                    selectedTheme === key
                      ? { borderColor: 'var(--color-brand)', boxShadow: '0 0 0 2px var(--color-needs-human-tint-bd)' }
                      : { borderColor: 'var(--color-border)' }
                  }
                >
                  <div className="flex gap-1 mb-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: theme.colors.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: theme.colors.secondary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: theme.colors.accent }}
                    />
                  </div>
                  <span className="text-xs font-medium">{theme.name}</span>
                  {selectedTheme === key && (
                    <Check className="w-3 h-3 inline ml-1" style={{ color: 'var(--color-needs-human-text)' }} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(config.colors).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <input
                  type="color"
                  value={value}
                  onChange={(e) => handleColorChange(key as keyof ColorScheme, e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer"
                  style={{ border: '1px solid var(--color-border)' }}
                />
                <div className="flex-1">
                  <label className="block text-xs capitalize" style={{ color: 'var(--color-text-muted)' }}>{key}</label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleColorChange(key as keyof ColorScheme, e.target.value)}
                    className="w-full text-sm px-2 py-1 rounded"
                    style={{ border: '1px solid var(--color-border-sub)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="p-6" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-base)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--color-text-secondary)' }}>Preview</h3>
          <div 
            className="rounded-xl p-6 shadow-lg"
            style={{ backgroundColor: config.colors.background }}
          >
            <div className="flex items-center gap-4 mb-4">
              {config.logo_url && (
                <img src={config.logo_url} alt="Logo" className="h-10" />
              )}
              <div>
                <h2 
                  className="text-xl font-bold"
                  style={{ color: config.colors.text }}
                >
                  {config.company_name || 'Your Company Name'}
                </h2>
                {config.company_tagline && (
                  <p className="text-sm" style={{ color: config.colors.text + '99' }}>
                    {config.company_tagline}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                className="px-4 py-2 rounded-lg font-semibold text-white"
                style={{ backgroundColor: config.colors.primary }}
              >
                Primary Button
              </button>
              <button
                className="px-4 py-2 rounded-lg font-semibold text-white"
                style={{ backgroundColor: config.colors.secondary }}
              >
                Secondary
              </button>
              <button
                className="px-4 py-2 rounded-lg font-semibold text-white"
                style={{ backgroundColor: config.colors.accent }}
              >
                Accent
              </button>
            </div>
            <div className="mt-4 flex gap-3">
              <span 
                className="px-3 py-1 rounded-full text-sm text-white"
                style={{ backgroundColor: config.colors.success }}
              >
                Success
              </span>
              <span 
                className="px-3 py-1 rounded-full text-sm text-white"
                style={{ backgroundColor: config.colors.warning }}
              >
                Warning
              </span>
              <span 
                className="px-3 py-1 rounded-full text-sm text-white"
                style={{ backgroundColor: config.colors.error }}
              >
                Error
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
